import React, { useState, useEffect, useRef } from "react";

const Chatbot = ({ userId, ageYears, ageDays, language, onChatComplete }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [questionIndex, setQuestionIndex] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [points, setPoints] = useState(0);
  const [dailyPercentage, setDailyPercentage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const chatContainerRef = useRef(null);
  const MAX_QUESTIONS = 7;

  // Utility to get completion message based on language
  const getCompletionMessage = (lang) => {
    switch (lang) {
      case "Hindi":
        return "आज के लिए आपका काम पूरा हो गया! कल फिर आएं!";
      case "Hinglish":
        return "Aaj ke liye done! Kal wapas aana for more fun!";
      default:
        return "You're all done for today! Come back tomorrow for more fun!";
    }
  };

  // Utility to get the token from localStorage
  const getToken = () => localStorage.getItem("token");

  // Utility to calculate dayOffset (same as backend)
  const calculateDayOffset = (ageDays) => {
    const totalDays = ageDays;
    return ((totalDays % 365) + 365) % 365 + 1;
  };

  // Utility to debounce a function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Initial check for chat completion and history
  useEffect(() => {
    console.log("Chatbot useEffect triggered with:", { userId, ageYears, ageDays, language });
    if (!userId || !ageYears || !ageDays || !language) {
      setError("Unable to load chat due to missing user information.");
      setIsComplete(true);
      return;
    }

    const controller = new AbortController();
    checkChatCompletion(controller.signal);

    return () => controller.abort();
  }, [userId, ageYears, ageDays, language]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      console.log("Scrolled to bottom, messages:", messages);
    } else {
      console.warn("Chat container ref is null");
    }
  }, [messages]);

  // Trigger onChatComplete when isComplete changes
  useEffect(() => {
    console.log("isComplete state changed:", isComplete);
    if (isComplete && onChatComplete) {
      console.log("Chat is complete, calling onChatComplete with points:", points, "dailyPercentage:", dailyPercentage);
      onChatComplete({ points, dailyPercentage });
    }
  }, [isComplete, onChatComplete, points, dailyPercentage]);

  // Check completion status and load chat history
  const checkChatCompletion = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      const currentDayOffset = calculateDayOffset(ageDays);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/openai/check-completion`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, ageYears, ageDays, language }),
        signal,
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 401 || errorData.error === "Token expired, please log in again") {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return;
        }
        if (res.status === 429) {
          throw new Error("Too many requests. Please wait a few minutes and try again.");
        }
        if (res.status === 504) {
          throw new Error("Request timed out. Please check your connection and try again.");
        }
        throw new Error(`Failed to check completion status: ${JSON.stringify(errorData)}`);
      }

      const data = await res.json();
      console.log("Check completion response:", JSON.stringify(data, null, 2));

      const { isComplete: isCompleteFromServer, messages: completionMessages = [] } = data;

      if (!Array.isArray(completionMessages)) {
        console.error("Invalid messages format:", completionMessages);
        throw new Error("Invalid chat history format from server.");
      }

      setIsComplete(isCompleteFromServer);
      if (isCompleteFromServer) {
        setMessages(
          completionMessages.length
            ? completionMessages
            : [{ sender: "AI", text: getCompletionMessage(language) }]
        );
        // Prefer direct points and dailyPercentage from response, fallback to parsing
        if (data.points != null) {
          setPoints(data.points);
          console.log("Points from response:", data.points);
        } else {
          const lastMessage = completionMessages[completionMessages.length - 1]?.text || "";
          const totalPointsMatch = lastMessage.match(/Total: (\d+)/) || [null, "0"];
          setPoints(parseInt(totalPointsMatch[1], 10));
        }
        if (data.dailyPercentage != null) {
          setDailyPercentage(data.dailyPercentage);
          console.log("Daily percentage from response:", data.dailyPercentage);
        } else {
          const lastMessage = completionMessages[completionMessages.length - 1]?.text || "";
          const percentageMatch =
            lastMessage.match(
              /(?:Your daily smartness score is|Tera daily smartness score|आपका दैनिक स्मार्टनेस स्कोर) (\d+)%!/
            ) || [null, "0"];
          setDailyPercentage(parseInt(percentageMatch[1], 10));
        }
      } else if (completionMessages.length) {
        setMessages(completionMessages);
        const aiMessagesCount = completionMessages.filter((msg) => msg.sender === "AI").length;
        setQuestionIndex(Math.min(aiMessagesCount + 1, MAX_QUESTIONS));
        console.log("Loaded chat history, questionIndex set to:", aiMessagesCount + 1);
      } else {
        startConversation();
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Failed to load chat history. Please try again!");
      console.error("Check completion error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Send message to API
  const sendMessageToAPI = async (inputText, currentQuestionIndex) => {
    setLoading(true);
    setError(null);

    const requestBody = {
      userId,
      input: inputText,
      questionIndex: currentQuestionIndex,
      ageYears,
      ageDays,
      language,
    };

    console.log("Sending request to /api/openai/daily-chat with body:", JSON.stringify(requestBody, null, 2));

    try {
      const token = getToken();
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/openai/daily-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        if (res.status === 401 || errorData.error === "Token expired, please log in again") {
          localStorage.removeItem("token");
          window.location.href = "/login";
          return null;
        }
        if (res.status === 429) {
          setError("Too many requests. Please wait a few minutes and try again.");
          return null;
        }
        if (res.status === 504) {
          setError("Request timed out. Please check your connection and try again.");
          return null;
        }
        const errorText = await res.text().catch(() => "Unknown error");
        throw new Error(`Error: ${res.status} ${res.statusText} - ${errorData.error || errorText}`);
      }

      const data = await res.json();
      console.log("Response from /api/openai/daily-chat:", data);
      if (!data.message) throw new Error("No response message received from server.");
      return data;
    } catch (err) {
      setError(err.message || "Failed to get a response from the AI. Please try again!");
      console.error("Fetch error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Start conversation if no messages exist
  const startConversation = async () => {
    console.log("Starting conversation...");
    if (messages.length > 0) {
      console.log("Messages already exist, skipping startConversation.");
      return;
    }
    const response = await sendMessageToAPI("", 1);
    if (response) {
      setMessages([{ sender: "AI", text: response.message }]);
      if (response.isSummary) {
        console.log("Summary received in startConversation, marking chat as complete");
        setIsComplete(true);
        if (response.points != null) {
          setPoints(response.points);
          console.log("Points from startConversation:", response.points);
        }
        if (response.dailyPercentage != null) {
          setDailyPercentage(response.dailyPercentage);
          console.log("Daily percentage from startConversation:", response.dailyPercentage);
        }
      }
    } else if (!error) {
      setError("Unable to start conversation. Check your connection or server.");
      setIsComplete(true);
    }
  };

  // Handle sending user message
  const handleSend = debounce(async () => {
    if (!input.trim() || isComplete || loading || questionIndex > MAX_QUESTIONS) {
      if (isComplete) {
        setMessages((prev) => [
          ...prev,
          { sender: "Kid", text: input },
          { sender: "AI", text: getCompletionMessage(language) },
        ]);
        setInput("");
      }
      return;
    }

    // Input validation
    const MAX_INPUT_LENGTH = 500;
    const ALLOWED_CHARS = /^[a-zA-Z0-9\s.,!?'\-()[\]{}@#$%^&*+=;:"<>?\\/]*$/;
    if (input.length > MAX_INPUT_LENGTH) {
      setError(`Input is too long. Maximum ${MAX_INPUT_LENGTH} characters allowed.`);
      return;
    }
    if (!ALLOWED_CHARS.test(input)) {
      setError("Input contains invalid characters. Use letters, numbers, and common punctuation.");
      return;
    }

    const userMessage = { sender: "Kid", text: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    const response = await sendMessageToAPI(input, questionIndex);
    if (response) {
      const aiMessage = { sender: "AI", text: response.message };
      setMessages((prev) => {
        console.log("Updated messages:", [...prev, aiMessage]);
        return [...prev, aiMessage];
      });

      const newIndex = Math.min(questionIndex + 1, MAX_QUESTIONS);
      setQuestionIndex(newIndex);
      console.log("Question index updated to:", newIndex);

      // Prefer direct points and dailyPercentage from response, fallback to parsing
      if (response.points != null) {
        setPoints(response.points);
        console.log("Points from response:", response.points);
      } else {
        const totalPointsMatch = response.message.match(/Total: (\d+)/);
        const earnedPointsMatch = response.message.match(/(?:You earned|Kamaaye|आपने कमाए) (\d+) points!/);
        if (totalPointsMatch) {
          const newPoints = parseInt(totalPointsMatch[1], 10);
          setPoints(newPoints);
          console.log("Total points parsed:", newPoints);
        } else if (earnedPointsMatch) {
          const earnedPoints = parseInt(earnedPointsMatch[1], 10);
          setPoints((prev) => {
            const updatedPoints = prev + earnedPoints;
            console.log(`Earned ${earnedPoints} points, new total: ${updatedPoints}`);
            return updatedPoints;
          });
        }
      }

      if (response.dailyPercentage != null) {
        setDailyPercentage(response.dailyPercentage);
        console.log("Daily percentage from response:", response.dailyPercentage);
      } else {
        const percentageMatch =
          response.message.match(
            /(?:Your daily smartness score is|Tera daily smartness score|आपका दैनिक स्मार्टनेस स्कोर) (\d+)%!/
          ) || [null, "0"];
        setDailyPercentage(parseInt(percentageMatch[1], 10));
      }

      if (response.isSummary) {
        console.log("Summary received, marking chat as complete");
        setIsComplete(true);
      } else if (newIndex >= MAX_QUESTIONS) {
        console.log("Reached max questions, requesting summary...");
        const summaryResponse = await sendMessageToAPI("", MAX_QUESTIONS);
        if (summaryResponse) {
          setMessages((prev) => [...prev, { sender: "AI", text: summaryResponse.message }]);
          if (summaryResponse.isSummary) {
            console.log("Summary received after max questions, marking chat as complete");
            setIsComplete(true);
          }
          if (summaryResponse.points != null) {
            setPoints(summaryResponse.points);
            console.log("Points from summary:", summaryResponse.points);
          } else {
            const totalPointsMatch = summaryResponse.message.match(/Total: (\d+)/) || [null, "0"];
            setPoints(parseInt(totalPointsMatch[1], 10));
          }
          if (summaryResponse.dailyPercentage != null) {
            setDailyPercentage(summaryResponse.dailyPercentage);
            console.log("Daily percentage from summary:", summaryResponse.dailyPercentage);
          } else {
            const percentageMatch =
              summaryResponse.message.match(
                /(?:Your daily smartness score is|Tera daily smartness score|आपका दैनिक स्मार्टनेस स्कोर) (\d+)%!/
              ) || [null, "0"];
            setDailyPercentage(parseInt(percentageMatch[1], 10));
          }
        } else {
          console.log("Failed to fetch summary, marking chat as complete anyway");
          setIsComplete(true);
        }
      }
    } else {
      console.log("Response failed, marking chat as complete to unblock UI");
      setIsComplete(true);
    }
  }, 300);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const today = new Date();
  const dayName = today.toLocaleString("default", { weekday: "long" });
  const date = today.getDate();
  const month = today.toLocaleString("default", { month: "long" });
  const year = today.getFullYear();

  return (
    <div className="chatbot-inner flex flex-col h-full">
      <style>{`
        :root {
          --cyber-green: #a8fd2d;
          --electric-cyan: #22c1c3;
          --cosmic-pink: #ff1493;
          --midnight-black: #000000;
          --glass-bg: rgba(10, 14, 43, 0.3);
          --card-blur: 12px;
          --dark-blue-start: #1e3a8a;
          --dark-blue-end: #3b82f6;
        }

        .chatbot-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          color: white;
        }

        .chat-header {
          padding: 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .chat-header h3 {
          font-size: 18px;
          font-weight: 600;
          background: linear-gradient(to right, var(--electric-cyan), var(--cyber-green));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .chat-header p {
          font-size: 12px;
          opacity: 0.8;
        }

        .theme-black .chat-header p {
          color: white;
        }

        .theme-white .chat-header p {
          color: #333;
        }

        .theme-pink .chat-header p {
          color: #333;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px 0;
          scrollbar-width: thin;
          scrollbar-color: var(--cyber-green) var(--midnight-black);
        }

        .chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .chat-messages::-webkit-scrollbar-track {
          background: var(--midnight-black);
        }

        .chat-messages::-webkit-scrollbar-thumb {
          background: var(--cyber-green);
          border-radius: 3px;
        }

        .message {
          margin: 12px 0;
          padding: 10px 14px;
          border-radius: 12px;
          max-width: 75%;
          position: relative;
          animation: slide-in 0.3s ease-out;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .message.ai-message::after,
        .message.kid-message::after {
          content: attr(data-timestamp);
          display: block;
          font-size: 10px;
          opacity: 0.6;
          margin-top: 4px;
          text-align: right;
        }

        .kid-message {
          background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
          color: white;
          margin-left: auto;
          border-bottom-right-radius: 0;
        }

        .ai-message {
          background: linear-gradient(45deg, var(--dark-blue-start), var(--dark-blue-end));
          color: white;
          margin-right: auto;
          border-bottom-left-radius: 0;
        }

        .chat-input {
          padding-top: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .chat-input input {
          flex: 1;
          padding: 10px 20px;
          border-radius: 50px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          outline: none;
          transition: all 0.3s ease;
          width: 100%;
          box-sizing: border-box;
        }

        .theme-black .chat-input input {
          border-color: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .theme-white .chat-input input {
          border-color: #333;
          color: #333;
        }

        .theme-pink .chat-input input {
          border-color: #333;
          color: #333;
        }

        .chat-input input::placeholder {
          color: inherit;
          opacity: 0.6;
        }

        .chat-input input:focus {
          box-shadow: 0 0 15px var(--cyber-green);
          border-color: var(--cyber-green);
        }

        .chat-input button {
          padding: 10px 20px;
          border-radius: 50px;
          background: linear-gradient(45deg, var(--electric-cyan), var(--cyber-green));
          border: none;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 80px;
        }

        .chat-input button:hover {
          box-shadow: 0 0 20px var(--cyber-green);
        }

        .chat-input button:disabled {
          background: linear-gradient(45deg, rgba(34, 193, 195, 0.5), rgba(168, 253, 45, 0.5));
          cursor: not-allowed;
        }

        .retry-button {
          margin-top: 8px;
          padding: 8px 16px;
          border-radius: 50px;
          background: linear-gradient(45deg, var(--cosmic-pink), var(--electric-cyan));
          border: none;
          color: white;
          font-weight: 600;
          cursor: pointer;
        }

        .retry-button:hover {
          box-shadow: 0 0 15px var(--cosmic-pink);
        }

        .loading-spinner {
          display: inline-block;
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: var(--cyber-green);
          animation: spin 1s ease-in-out infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .points {
          padding: 12px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .points h4 {
          font-size: 14px;
          font-weight: 600;
          background: linear-gradient(to right, var(--electric-cyan), var(--cyber-green));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .points p {
          font-size: 12px;
          color: var(--cyber-green);
          margin-top: 4px;
        }

        .error {
          color: var(--cosmic-pink);
          font-size: 12px;
          padding: 8px 0;
          text-align: center;
        }

        .complete-message {
          font-size: 14px;
          opacity: 0.8;
          text-align: center;
          padding: 16px 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        @keyframes slide-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 768px) {
          .chat-header h3 {
            font-size: 16px;
          }

          .chat-header p {
            font-size: 10px;
          }

          .message {
            max-width: 80%;
            padding: 8px 12px;
          }

          .chat-input {
            gap: 8px;
          }

          .chat-input input {
            padding: 8px 16px;
            font-size: 14px;
          }

          .chat-input button {
            padding: 8px 16px;
            font-size: 14px;
          }
        }
      `}</style>

      <div className="chat-header">
        <h3>{language === "Hindi" ? "दैनिक चैट" : language === "Hinglish" ? "Daily Chat" : "Daily Chat"}</h3>
        <p>{`${dayName}, ${date} ${month} ${year}`}</p>
      </div>

      <div ref={chatContainerRef} className="chat-messages" role="log" aria-live="polite">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`message ${msg.sender === "Kid" ? "kid-message" : "ai-message"}`}
            data-timestamp={new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          >
            <span>{msg.text}</span>
          </div>
        ))}
        {loading && (
          <div className="message ai-message">
            <span>
              <span className="loading-spinner"></span> Thinking...
            </span>
          </div>
        )}
      </div>

      <div className="chat-input">
        {error && (
          <div className="error">
            {error}
            <button
              onClick={() => checkChatCompletion(new AbortController().signal)}
              className="retry-button"
              aria-label="Retry loading chat"
            >
              Retry
            </button>
          </div>
        )}
        {isComplete ? (
          <div className="complete-message">
            <p>
              {language === "Hindi"
                ? "याय! आज के लिए आपका काम पूरा हुआ! कल फिर आएं!"
                : language === "Hinglish"
                ? "Yay! Aaj ke liye done! Kal wapas aao!"
                : "Yay! You’re done for today! Come back tomorrow!"}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", width: "100%", gap: "12px" }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                language === "Hindi" ? "यहाँ टाइप करें..." : language === "Hinglish" ? "Yahan type karo..." : "Type here..."
              }
              disabled={loading || isComplete || questionIndex > MAX_QUESTIONS}
              aria-label="Chat input"
            />
            <button
              onClick={handleSend}
              disabled={loading || isComplete || questionIndex > MAX_QUESTIONS}
              aria-label={loading ? "Sending message" : "Send message"}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : language === "Hindi" ? (
                "भेजें"
              ) : language === "Hinglish" ? (
                "Send"
              ) : (
                "Send"
              )}
            </button>
          </div>
        )}
      </div>

      {(points > 0 || dailyPercentage > 0) && (
        <div className="points">
          <h4>{language === "Hindi" ? "आपके अंक" : language === "Hinglish" ? "Tere Points" : "Your Points"}</h4>
          <p>
            {language === "Hindi"
              ? `कुल: ${points} | दैनिक स्मार्टनेस: ${dailyPercentage}%`
              : language === "Hinglish"
              ? `Total: ${points} | Daily Smartness: ${dailyPercentage}%`
              : `Total: ${points} | Daily Smartness: ${dailyPercentage}%`}
          </p>
        </div>
      )}
    </div>
  );
};

export default React.memo(Chatbot);